import { useState, useRef, useEffect } from "react";
import { Stack, useTheme } from "@mui/material";

import TicketsChatSkeleton from "./TicketsChatSkeleton";
import TicketsSidebar from "./TicketsSidebar";
import TicketsChatPanel from "./TicketsChatPanel";
import {
  MAIN_CTA_OPTIONS,
  BOT_RESPONSES,
  SUB_RESPONSES,
  FALLBACK_RESPONSE,
} from "../Chat/AraChatbot/chatbotConfig";

export default function Ticket() {

  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  // Static tickets commented out — only chatbot localStorage users shown
  // const staticTickets = [
  //   { id: 1, name: "Support Bot", tag: "Created", color: theme.palette.primary.main, subtitle: "How can I help you today?" },
  //   { id: 2, name: "Help Center", tag: "Pending", color: "#F59E0B", subtitle: "Ticket #847 closed" },
  //   { id: 3, name: "Telecalling Team", tag: "Active", color: "#EC4899", subtitle: "Waiting for response..." },
  //   { id: 4, name: "Lead Followups", tag: "Pending", color: "#EF4444", subtitle: "Assigned to Sarah M." },
  //   { id: 5, name: "Customer Support", tag: "Closed", color: "#10B981", subtitle: "No messages yet" },
  // ];

  const [chatbotTickets, setChatbotTickets] = useState([]);
  const [showWelcomeForm, setShowWelcomeForm] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const [selectedChat, setSelectedChat] = useState(null);
  const [chatStore, setChatStore] = useState({});
  const [unread, setUnread] = useState({});
  const [isTyping, setIsTyping] = useState(false);

  // Load all chatbot users from localStorage and create sidebar entries
  useEffect(() => {
    const loadChatbotData = () => {
      const raw = localStorage.getItem("ara_chatbot_users");
      if (!raw) { setChatbotTickets([]); return; }

      const users = JSON.parse(raw);
      if (!users.length) { setChatbotTickets([]); return; }

      const entries = [];
      const storeUpdates = {};

      users.forEach((user) => {
        const chatId = `cb_${user.phone}`;
        const msgsRaw = localStorage.getItem(`ara_chatbot_msgs_${user.phone}`);
        const msgs = msgsRaw ? JSON.parse(msgsRaw) : [];
        const lastMsg = msgs[msgs.length - 1];

        entries.push({
          id: chatId,
          name: user.name || user.phone,
          tag: "Chatbot",
          color: theme.palette.primary.main,
          subtitle: lastMsg
            ? (lastMsg.text.length > 40 ? lastMsg.text.slice(0, 40) + "..." : lastMsg.text)
            : "Chatbot conversation",
          isChatbot: true,
          phone: user.phone,
        });

        // Convert chatbot messages (sender: bot/user) → ticket format (from: agent/user)
        if (msgs.length > 0) {
          storeUpdates[chatId] = msgs.map((msg) => ({
            from: msg.sender === "bot" ? "agent" : "user",
            type: "text",
            text: msg.text,
            createdAt: new Date(),
            time: msg.time,
            status: msg.sender === "user" ? "read" : undefined,
          }));
        }
      });

      setChatbotTickets(entries);
      if (Object.keys(storeUpdates).length > 0) {
        setChatStore((prev) => {
          const next = { ...prev };
          for (const [key, val] of Object.entries(storeUpdates)) {
            // Only load from localStorage if chat not yet in store
            // (preserves new messages sent in ticket chat)
            if (!prev[key]) {
              next[key] = val;
            }
          }
          return next;
        });
      }
    };

    loadChatbotData();
    const interval = setInterval(loadChatbotData, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only chatbot localStorage users
  const tickets = [...chatbotTickets];

  const [search, setSearch] = useState("");

  const filteredTickets = tickets.filter((t) => {
    const q = search.toLowerCase();
    if (t.name.toLowerCase().includes(q)) return true;
    if (t.phone && t.phone.includes(q)) return true;
    return false;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const messages = selectedChat
    ? chatStore[String(selectedChat.id)] || []
    : [];

  const [input, setInput] = useState("");
  const chatRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (chatRef.current)
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // Dynamic quick replies from Ara chatbot config
  const [quickReplies, setQuickReplies] = useState(
    MAIN_CTA_OPTIONS.map((o) => o.label)
  );

  const openChat = (chat) => {
    const chatId = String(chat.id);
    setSelectedChat(chat);
    setUnread(prev => ({ ...prev, [chatId]: 0 }));
    setShowWelcomeForm(false);
    setShowBooking(false);

    // Reset quick replies to main CTAs when switching chat
    setQuickReplies(MAIN_CTA_OPTIONS.map((o) => o.label));

    // For chatbot entries, messages are pre-loaded from localStorage
    // Add Ara chatbot greeting if no messages exist yet
    setChatStore(prev => {
      if (prev[chatId] && prev[chatId].length > 0) return prev;

      return {
        ...prev,
        [chatId]: [
          {
            from: "agent",
            type: "text",
            text: `Hi ${chat.name}! 👋 Welcome to Ara Digital Marketing. I'm here to help you grow your business online. What would you like to explore?`,
            createdAt: new Date(),
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit"
            })
          }
        ]
      };
    });
  };

  // Start new chat → show welcome form
  const handleStartNewChat = () => {
    setSelectedChat(null);
    setShowWelcomeForm(true);
    setShowBooking(false);
  };

  // Welcome form submitted → create user in localStorage, open chat
  const handleWelcomeSubmit = (userData) => {
    // Save to localStorage (multi-user array)
    const existing = JSON.parse(localStorage.getItem("ara_chatbot_users") || "[]");
    existing.push({ ...userData, createdAt: new Date().toISOString() });
    localStorage.setItem("ara_chatbot_users", JSON.stringify(existing));
    localStorage.setItem("ara_chatbot_user", JSON.stringify(userData));

    // Create sidebar entry + open chat immediately
    const chatId = `cb_${userData.phone}`;
    const newEntry = {
      id: chatId,
      name: userData.name,
      tag: "Chatbot",
      color: theme.palette.primary.main,
      subtitle: "Chatbot conversation",
      isChatbot: true,
      phone: userData.phone,
    };

    setChatbotTickets(prev => [...prev, newEntry]);
    setShowWelcomeForm(false);
    openChat(newEntry);
  };

  // Booking form submitted → save to localStorage + add messages
  const handleBookingSubmit = (data) => {
    setShowBooking(false);
    if (!selectedChat) return;

    const chatId = String(selectedChat.id);

    // Save booking to localStorage
    const prev = JSON.parse(localStorage.getItem("ara_chatbot_bookings") || "[]");
    localStorage.setItem("ara_chatbot_bookings", JSON.stringify([...prev, data]));

    const now = new Date();
    const userMsg = {
      from: "user",
      type: "text",
      text: `📅 Booking: ${data.name}, ${data.phone}, ${data.date} at ${data.time}`,
      status: "read",
      createdAt: now,
      time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    const statusMsg = data.status === "success"
      ? `Your meeting has been booked for ${data.date} at ${data.time} and added to our pipeline! ✅ Our team will call you at ${data.phone} to confirm.`
      : `Your booking request for ${data.date} at ${data.time} has been received! Our team will reach out to you at ${data.phone} shortly.`;

    const botMsg = {
      from: "agent",
      type: "text",
      text: statusMsg,
      createdAt: new Date(),
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    setChatStore(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), userMsg, botMsg]
    }));

    setQuickReplies(MAIN_CTA_OPTIONS.map((o) => o.label));
  };

  const updateLastUserStatus = (status) => {
    if (!selectedChat) return;

    const chatId = String(selectedChat.id);

    setChatStore(prev => {
      const list = [...(prev[chatId] || [])];

      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].from === "user") {
          list[i] = { ...list[i], status };
          break;
        }
      }

      return { ...prev, [chatId]: list };
    });
  };

  const sendUserMessage = (text) => {
    if (!text.trim() || !selectedChat) return;

    const chatId = String(selectedChat.id);
    const now = new Date();

    const userMsg = {
      from: "user",
      type: "text",
      text,
      status: "sending",
      createdAt: now,
      time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    // Add user message immediately
    setChatStore(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), userMsg]
    }));

    setUnread(prev => ({
      ...prev,
      [chatId]:
        selectedChat && String(selectedChat.id) === chatId
          ? 0
          : (prev[chatId] || 0) + 1
    }));

    setInput("");

    // Check if "Book a Meeting" was clicked → show booking form instead of bot reply
    let isBooking = false;
    for (const key of Object.keys(BOT_RESPONSES)) {
      const opts = BOT_RESPONSES[key].options || [];
      const match = opts.find((o) => o.label === text && o.id === "ct_meeting");
      if (match) { isBooking = true; break; }
    }

    if (isBooking) {
      setShowBooking(true);
      setTimeout(() => updateLastUserStatus("read"), 500);
      return;
    }

    setIsTyping(true);

    // Bot responds after typing delay — Ara chatbot CTA logic
    setTimeout(() => {
      let botResponse = FALLBACK_RESPONSE;
      let nextReplies = MAIN_CTA_OPTIONS.map((o) => o.label);

      // Check if text matches a main CTA label
      const mainCta = MAIN_CTA_OPTIONS.find((o) => o.label === text);
      if (mainCta && BOT_RESPONSES[mainCta.id]) {
        botResponse = BOT_RESPONSES[mainCta.id].message;
        const subOpts = BOT_RESPONSES[mainCta.id].options;
        if (subOpts) {
          nextReplies = subOpts.map((o) => o.label);
        }
      } else {
        // Check if text matches a sub-option label
        let subId = null;
        for (const key of Object.keys(BOT_RESPONSES)) {
          const opts = BOT_RESPONSES[key].options || [];
          const match = opts.find((o) => o.label === text);
          if (match) { subId = match.id; break; }
        }
        if (subId && SUB_RESPONSES[subId]) {
          botResponse = SUB_RESPONSES[subId];
          nextReplies = MAIN_CTA_OPTIONS.map((o) => o.label);
        }
      }

      setQuickReplies(nextReplies);

      const botTime = new Date();

      const botMsg = {
        from: "agent",
        type: "text",
        text: botResponse,
        createdAt: botTime,
        time: botTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      };

      setChatStore(prev => ({
        ...prev,
        [chatId]: [...(prev[chatId] || []), botMsg]
      }));

      setIsTyping(false);
    }, 1800);

    setTimeout(() => updateLastUserStatus("sent"), 700);
    setTimeout(() => updateLastUserStatus("delivered"), 1500);
    setTimeout(() => updateLastUserStatus("read"), 3000);

    if (Math.random() < 0.18)
      setTimeout(() => updateLastUserStatus("failed"), 1000);
  };

  const sendImageMessage = (file) => {
    if (!file || !selectedChat) return;

    const chatId = String(selectedChat.id);
    const now = new Date();
    const url = URL.createObjectURL(file);

    const imgMsg = {
      from: "user",
      type: "image",
      image: url,
      status: "sending",
      createdAt: now,
      time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    setChatStore(prev => ({
      ...prev,
      [chatId]: [
        ...(prev[chatId] || []),
        imgMsg
      ]
    }));

    setUnread(prev => ({
      ...prev,
      [chatId]: (prev[chatId] || 0) + 1
    }));

    setTimeout(() => updateLastUserStatus("sent"), 700);
    setTimeout(() => updateLastUserStatus("delivered"), 1500);
    setTimeout(() => updateLastUserStatus("read"), 3000);

    if (Math.random() < 0.18)
      setTimeout(() => updateLastUserStatus("failed"), 1000);
  };

  // eslint-disable-next-line no-unused-vars
  const sendDocumentMessage = (file) => {
    if (!file || !selectedChat) return;

    const chatId = String(selectedChat.id);
    const now = new Date();
    const url = URL.createObjectURL(file);

    const sizeKB = (file.size / 1024).toFixed(1);
    const fileSize = sizeKB >= 1024
      ? `${(sizeKB / 1024).toFixed(1)} MB`
      : `${sizeKB} KB`;

    const docMsg = {
      from: "user",
      type: "document",
      fileName: file.name,
      fileUrl: url,
      fileSize,
      status: "sending",
      createdAt: now,
      time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

    setChatStore(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), docMsg]
    }));

    setTimeout(() => updateLastUserStatus("sent"), 700);
    setTimeout(() => updateLastUserStatus("delivered"), 1500);
    setTimeout(() => updateLastUserStatus("read"), 3000);
  };

  const retryMessage = () => {
    updateLastUserStatus("sending");
    setTimeout(() => updateLastUserStatus("sent"), 600);
    setTimeout(() => updateLastUserStatus("delivered"), 1400);
    setTimeout(() => updateLastUserStatus("read"), 2600);
  };

  const getDayLabel = (date) => {
    const d = new Date(date);
    const today = new Date();
    const y = new Date();
    y.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === y.toDateString()) return "Yesterday";

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) return <TicketsChatSkeleton />;

  return (
    <Stack direction="row" sx={{ height: "100vh" }}>
      <TicketsSidebar
        theme={theme}
        search={search}
        setSearch={setSearch}
        filteredTickets={filteredTickets}
        selectedChat={selectedChat}
        openChat={openChat}
        chatStore={chatStore}
        unread={unread}
      />

      <TicketsChatPanel
        theme={theme}
        isTyping={isTyping}
        chatRef={chatRef}
        fileInputRef={fileInputRef}
        selectedChat={selectedChat}
        messages={messages}
        input={input}
        setInput={setInput}
        sendUserMessage={sendUserMessage}
        sendImageMessage={sendImageMessage}
        sendDocumentMessage={sendDocumentMessage}
        retryMessage={retryMessage}
        quickReplies={quickReplies}
        getDayLabel={getDayLabel}
        tickets={tickets}
        openChat={openChat}
        showWelcomeForm={showWelcomeForm}
        onStartNewChat={handleStartNewChat}
        onWelcomeSubmit={handleWelcomeSubmit}
        showBooking={showBooking}
        onBookingSubmit={handleBookingSubmit}
        onBookingCancel={() => setShowBooking(false)}
      />

    </Stack>
  );
}
